import {Component, ElementRef, OnInit, ViewChild, TemplateRef, AfterViewInit, DestroyRef} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {ActivatedRoute, Router} from '@angular/router';
import {Location} from '@angular/common';
import {Title} from '@angular/platform-browser';
import {EmailService} from '../../../../Services/email.service';
import {AppConfigService} from '../../../../Services/app-config.service';
import {BsToastService} from '../../../../Services/bs-toast-service';
import {EmailModel} from '../../../../DataModels/email-model';
import {StripePaymentService} from '../../../../Services/stripe-payment.service';
import {DataService} from '../../../../Services/data.service';
import {ResponseModel} from '../../../../DataModels/response-model';
import {lastValueFrom} from 'rxjs';
import {Modal} from 'bootstrap';
import {DateFilterService} from '../../../../Services/dateFilter.service';
import {StripeConnectApService} from '../../../../Services/stripe-connect-ap.service';
import {ReportPrintService} from '../../../../Services/report-print.service';

@Component({
  selector: 'app-stripe-transaction-detail',
  templateUrl: './stripe-transaction-detail.component.html',
  styleUrls: ['./stripe-transaction-detail.component.css'],
  standalone: false,
})
export class StripeTransactionDetailComponent implements OnInit, AfterViewInit {
  @ViewChild('PrintRoot', {static: false}) PrintRoot: ElementRef;

  // View-model mirrors existing reports approach
  vm: any = {
    // Use stripeReport to avoid conflicts with ambient "report" symbols
    stripeReport: [],
    // Keep report as a mirror for shared components that expect vm.report for enable/disable logic
    report: [],
    request: {
      fromDate: null,
      toDate: null,
      entity_id: null,
    },
    fromDateSelect: null,
    toDateSelect: null,
    // Additional filters expected by backend
    stripeFlowType: 'All',
    status: 'All',
    // Filters and paging indices used by child selectors
    entityFilter: {name: ''},
    startEntity: 0,
    entity: [],
    // List of {name} objects used by ngbTypeahead in EntitySelect
    entityName: [],
    // The displayed text value in the entity input (two-way bound by EntitySelect)
    entityInput: '',
    company: [],
    buttonsDisabled: false,
    showSpecificDates: true,
    showFilters: false,
    search: '',
    page: 1,
    pageSize: 20,
    totalCount: 0,
  };

  // Email modal integration and standard header button config
  reportDataToSendInEmail: EmailModel = new EmailModel();
  buttonConfig: any = {
    reportFormat: false,
    print: false,
    printReport: true,
    email: true,
    update: true,
    exportExcel: false,
    close: true,
    filters: false,
    clear: false,
    disableOnEmpty: true,
  };

  // UI helpers
  loading = false;
  errorMessage = '';
  cancellingTransferId: string | null = null;
  // Determine AR/AP behavior based on route query params (type/subLedger); default AR
  ledgerMode: 'AR' | 'AP' = 'AR';
  // Drawer/detail state
  selectedRow: any = null;
  drawerOpen = false;
  drawerTab: 'summary' | 'amounts' | 'payment' | 'related' | 'json' = 'summary';

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private location: Location,
    private titleService: Title,
    private emailService: EmailService,
    private appConfig: AppConfigService,
    private notification: BsToastService,
    private stripeService: StripePaymentService,
    private tableDataService: DataService,
    private dateFilter: DateFilterService,
    private stripeApService: StripeConnectApService,
    private reportPrint: ReportPrintService,
    private destroyRef: DestroyRef,
  ) {
    this.titleService.setTitle('Stripe Transaction Detail');
  }

  ngOnInit(): void {
    // If query params are supplied, honor them
    this.route.queryParams.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((qp) => {
      const typeRaw = (qp?.type || qp?.subLedger || '').toString().trim().toLowerCase();
      this.ledgerMode = typeRaw === 'ap' ? 'AP' : 'AR';
      if (qp?.fromDate) {
        this.vm.request.fromDate = qp.fromDate;
      }
      if (qp?.toDate) {
        this.vm.request.toDate = qp.toDate;
      }
      if (qp?.entityId) {
        this.vm.request.entity_id = qp.entityId;
      }
      if (qp?.stripeFlowType) {
        this.vm.stripeFlowType = qp.stripeFlowType;
      }
      if (qp?.status) {
        this.vm.status = qp.status;
      }
    });

    // Initialize selectable month lists and default selected values similar to Payments
    const init = this.dateFilter.DateFilterInit(new Date().toISOString(), new Date().toISOString());
    this.vm.fromDateSelect = init.fromDateSelect;
    this.vm.toDateSelect = init.toDateSelect;

    // Default range rule:
    // - To Date: the period (month) containing today (i.e., end of current month option)
    // - From Date: the period (month) containing the date that is 90 days prior (i.e., first day of that month option)
    const today = new Date();
    const ninetyDaysAgo = new Date();
    ninetyDaysAgo.setDate(ninetyDaysAgo.getDate() - 90);

    const currentMonthRange = this.dateFilter.GetDefaultCurrentMonthRange(this.vm.fromDateSelect, this.vm.toDateSelect, today);
    const fromMonthRange = this.dateFilter.GetDefaultCurrentMonthRange(this.vm.fromDateSelect, this.vm.toDateSelect, ninetyDaysAgo);

    if (!this.vm.request.fromDate) {
      this.vm.request.fromDate = fromMonthRange.fromDate;
    }
    if (!this.vm.request.toDate) {
      this.vm.request.toDate = currentMonthRange.toDate;
    }
    // Start with selects mode (not datepicker) like Payments
    this.vm.showSpecificDates = false;

    // Load selector backing data (entity list for typeahead)
    this.loadFilterOptions();
  }

  // DataTable column templates
  @ViewChild('dateHeaderTpl', {static: false}) dateHeaderTpl?: TemplateRef<any>;
  @ViewChild('lastHeaderTpl', {static: false}) lastHeaderTpl?: TemplateRef<any>;
  @ViewChild('createdTpl', {static: false}) createdTpl?: TemplateRef<any>;
  @ViewChild('updatedTpl', {static: false}) updatedTpl?: TemplateRef<any>;
  @ViewChild('entityTpl', {static: false}) entityTpl?: TemplateRef<any>;
  @ViewChild('statusTpl', {static: false}) statusTpl?: TemplateRef<any>;
  @ViewChild('intentTpl', {static: false}) intentTpl?: TemplateRef<any>;
  @ViewChild('paymentIntentTpl', {static: false}) paymentIntentTpl?: TemplateRef<any>;
  @ViewChild('cancellableTpl', {static: false}) cancellableTpl?: TemplateRef<any>;
  @ViewChild('missingTpl', {static: false}) missingTpl?: TemplateRef<any>;

  dataTableColumns: any[] = [];

  ngAfterViewInit(): void {
    // Build DataTable columns with exact mappings; use templates for formatted cells
    this.dataTableColumns = [
      // Hidden stash column for AppTransactionId (used to build payment link)
      {header: 'AppTransactionId', field: 'AppTransactionId', class: 'd-none', orderable: false},
      {
        header: 'Date',
        field: 'CreatedUtc',
        class: '',
        headerTemplate: this.dateHeaderTpl || null,
        template: this.createdTpl || null,
        orderable: true
      },
      {
        header: 'Last',
        field: 'UpdatedUtc',
        class: '',
        headerTemplate: this.lastHeaderTpl || null,
        template: this.updatedTpl || null,
        orderable: true
      },
      {header: 'Entity', field: 'AppEntityId', class: '', template: this.entityTpl || null, orderable: true},
      {header: 'Status', field: 'Status', class: '', template: this.statusTpl || null, orderable: true},
      {header: 'Direction', field: 'Direction', class: '', orderable: true},
      {header: 'Flow', field: 'FlowType', class: '', orderable: true},
      {header: 'Amount', field: 'Amount', class: 'text-end', pipe: 'number', digitsInfo: '1.2-2', orderable: true},
      {header: 'Fee', field: 'StripeFeeAmount', class: 'text-end', pipe: 'number', digitsInfo: '1.2-2', orderable: true},
      {header: 'Net', field: 'StripeNetAmount', class: 'text-end', pipe: 'number', digitsInfo: '1.2-2', orderable: true},
      {header: 'Currency', field: 'Currency', class: '', orderable: true},
      {header: 'App Bank', field: 'AppBankName', class: '', orderable: true},
      ...(this.ledgerMode === 'AP'
          ? [{header: 'Transfer Intent', field: 'StripeTransferId', class: '', orderable: false, template: this.intentTpl || null}]
          : [{
            header: 'Payment Intent',
            field: 'StripePaymentIntentId',
            class: '',
            orderable: false,
            template: this.paymentIntentTpl || null
          }]
      ),
      {header: 'Cancel', field: 'IsCancellable', class: '', orderable: false, template: this.cancellableTpl || null},
      {header: 'Missing', field: 'IsMissingLocalTransaction', class: '', orderable: false, template: this.missingTpl || null},
    ];
  }

  buildPaymentUrl(appTransactionId: any): string {
    const id = String(appTransactionId || '').trim();
    if (!id) {
      return '/transactions/payment/form';
    }
    return `/transactions/payment/form?paymentId=${encodeURIComponent(id)}`;
  }

  // For laptop browser use-case: always open a new tab via window.open on click
  openPaymentLink(evt: MouseEvent, appTransactionId: any): void {
    evt.preventDefault();
    const url = this.buildPaymentUrl(appTransactionId);
    window.open(url, '_blank', 'noopener');
  }

  // Header buttons
  update(): void {
    this.load();
  }

  clear(): void {
    this.vm.request = {fromDate: null, toDate: null, entity_id: null};
    this.vm.fromDateSelect = null;
    this.vm.toDateSelect = null;
    this.vm.entityFilter = {name: ''};
    this.vm.startEntity = 0;
    this.vm.entityInput = '';
    this.vm.search = '';
    this.vm.page = 1;
    this.vm.pageSize = 20;
    this.vm.stripeReport = [];
    this.vm.report = [];
    this.vm.totalCount = 0;
    this.errorMessage = '';
  }

  close(): void {
    this.location.back();
  }

  private normalizeDate(value: any): string | undefined {
    if (!value) {
      return undefined;
    }
    if (typeof value === 'object' && Number.isFinite(value.year) && Number.isFinite(value.month) && Number.isFinite(value.day)) {
      const y = String(value.year).padStart(4, '0');
      const m = String(value.month).padStart(2, '0');
      const d = String(value.day).padStart(2, '0');
      return `${y}-${m}-${d}`;
    }
    const raw = String(value || '').trim();
    if (!raw) {
      return undefined;
    }
    if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
      return raw;
    }
    const dt = new Date(raw);
    if (!Number.isNaN(dt.getTime())) {
      return dt.toISOString().slice(0, 10);
    }
    return undefined;
  }

  async load(): Promise<void> {
    const appEntityIdRaw = this.vm.request.entity_id;
    // No bank account filter for this report
    const appBankIdRaw = '';
    // For payments endpoint, always send both keys; use empty string when not selected (bank id forced empty)
    const appEntityId = (appEntityIdRaw == null) ? '' : String(appEntityIdRaw).trim();
    const appBankId = (appBankIdRaw == null) ? '' : String(appBankIdRaw).trim();
    const fromDate = this.normalizeDate(this.vm.request.fromDate ?? this.vm.fromDateSelect);
    const toDate = this.normalizeDate(this.vm.request.toDate ?? this.vm.toDateSelect);

    this.loading = true;
    this.errorMessage = '';
    try {
      // Mirror admin form Transactions tab behavior
      // AR: use getTransactions(appEntityId, flowType default 1, take=pageSize, status, from, to) -> array (not paged)
      // AP: use getAllPaymentsByEntity(appEntityId, page, pageSize, from, to, status) -> paged object
      if (!appEntityId) {
        this.vm.stripeReport = [];
        this.vm.report = [];
        this.vm.totalCount = 0;
        return;
      }

      if (this.ledgerMode === 'AP') {
        const result = await lastValueFrom(this.stripeService.getAllPaymentsByEntity(
          appEntityId,
          this.vm.page,
          this.vm.pageSize,
          fromDate,
          toDate,
          this.vm.status || 'All',
        ));

        // eslint-disable-next-line no-console
        console.log('[StripeTransactionDetail][AP] getAllPaymentsByEntity params', {
          appEntityId,
          page: this.vm.page,
          pageSize: this.vm.pageSize,
          fromDate,
          toDate,
          status: this.vm.status || 'All',
        });
        // eslint-disable-next-line no-console
        console.log('[StripeTransactionDetail][AP] raw response', result);

        const rows = result?.data || result?.Data || result?.items || result?.Items || result?.rows || result?.Rows || [];
        const normalized = (Array.isArray(rows) ? rows : []).map((r: any) => ({
          ...r,
          AppBankName: r?.AppBankName ?? r?.appBankName ?? '',
          Direction: r?.Direction ?? r?.direction ?? '',
          FlowType: r?.FlowType ?? r?.flowType ?? '',
          Amount: Number(r?.Amount ?? r?.amount ?? 0),
          StripeFeeAmount: r?.StripeFeeAmount ?? r?.stripeFeeAmount,
          Status: r?.Status ?? r?.status ?? '',
          StripePayoutId: r?.StripePayoutId ?? r?.stripePayoutId ?? r?.stripePayoutID ?? r?.stripePayout ?? null,
          StripeTransferId: r?.StripeTransferId ?? r?.stripeTransferId ?? r?.stripeTransferID ?? r?.stripeTransfer ?? null,
          StripePaymentIntentId: r?.StripePaymentIntentId ?? r?.stripePaymentIntentId ?? null,
          AppT_headerId: r?.AppT_headerId ?? r?.appT_headerId ?? r?.AppTHeaderId ?? r?.appTHeaderId ?? null,
          CreatedUtc: r?.CreatedUtc ?? r?.createdUtc ?? r?.createdAtUtc ?? null,
          UpdatedUtc: r?.UpdatedUtc ?? r?.updatedUtc ?? r?.updatedAtUtc ?? null,
        }));
        this.vm.stripeReport = normalized;
        this.vm.report = normalized;
        this.vm.totalCount = Number(result?.totalCount ?? result?.TotalCount ?? this.vm.stripeReport.length ?? 0);
        this.vm.page = Number(result?.page ?? result?.Page ?? this.vm.page);
        this.vm.pageSize = Number(result?.pageSize ?? result?.PageSize ?? this.vm.pageSize);
      } else {
        // AR mode
        const flowRaw = this.vm.stripeFlowType;
        let flowType: any = flowRaw;
        if (flowType === undefined || flowType === null || String(flowType).trim() === '' || String(flowType).toLowerCase() === 'all') {
          flowType = 1;
        }

        const result = await lastValueFrom(this.stripeService.getTransactions(
          appEntityId,
          Number(flowType),
          this.vm.page,
          this.vm.pageSize,
          this.vm.status || null,
          fromDate,
          toDate,
        ));

        // eslint-disable-next-line no-console
        console.log('[StripeTransactionDetail][AR] getTransactions params', {
          appEntityId,
          flowType: Number(flowType),
          page: this.vm.page,
          pageSize: this.vm.pageSize,
          status: this.vm.status || null,
          fromDate,
          toDate,
        });
        // eslint-disable-next-line no-console
        console.log('[StripeTransactionDetail][AR] raw response', result);

        const rows: any[] = (result as any)?.data || (result as any)?.Data || [];
        const normalizedAr = (Array.isArray(rows) ? rows : []).map((r: any) => ({
          ...r,
          StripePaymentIntentId: r?.StripePaymentIntentId ?? r?.stripePaymentIntentId ?? null,
          AppT_headerId: r?.AppT_headerId ?? r?.appT_headerId ?? r?.AppTHeaderId ?? r?.appTHeaderId ?? null,
        }));
        this.vm.stripeReport = normalizedAr;
        this.vm.report = normalizedAr;
        this.vm.totalCount = Number((result as any)?.totalCount ?? (result as any)?.TotalCount ?? normalizedAr.length);
        this.vm.page = Number((result as any)?.page ?? (result as any)?.Page ?? this.vm.page);
        this.vm.pageSize = Number((result as any)?.pageSize ?? (result as any)?.PageSize ?? this.vm.pageSize);
      }
    } catch (err: any) {
      const apiError = err?.error?.Message || err?.error?.message || err?.message || 'Failed to load Stripe payments.';
      this.errorMessage = apiError;
      this.notification.showErrorToast(apiError);
      this.vm.stripeReport = [];
      this.vm.report = [];
      this.vm.totalCount = 0;
    } finally {
      this.loading = false;
    }
  }

  async onCancelTransfer(row: any): Promise<void> {
    const transferId = (row?.StripeTransferId ?? row?.stripeTransferId ?? '').toString().trim();
    const appEntityId = (this.vm.request.entity_id || '').toString().trim();
    if (!transferId || !appEntityId) {
      this.notification.showErrorToast('Missing Transfer ID or Entity ID for cancellation.');
      return;
    }
    this.cancellingTransferId = transferId;
    try {
      const result = await lastValueFrom(this.stripeApService.reverseTransfer({
        appEntityId,
        transferId,
        reason: 'Cancelled by user',
      }));
      if (result?.blocked) {
        this.notification.showErrorToast(result.message || 'Transfer cannot be reversed.');
        return;
      }
      if (result?.reversed) {
        row.Status = result.status || 'Canceled';
        row.status = result.status || 'Canceled';
        row.IsCancellable = false;
        row.isCancellable = false;
        this.notification.showSuccessToast(result.message || 'Transfer successfully reversed.');
      } else {
        this.notification.showWarningToast(result?.message || 'Reversal did not complete.');
      }
    } catch (e: any) {
      const msg = e?.error?.Message || e?.error?.message || e?.message || 'Failed to cancel transfer.';
      this.notification.showErrorToast(msg);
    } finally {
      this.cancellingTransferId = null;
    }
  }

  // Standard ngb-pagination helpers
  get startIndex(): number {
    const tc = Number(this.vm.totalCount || 0);
    const ps = Number(this.vm.pageSize || 1);
    const pg = Number(this.vm.page || 1);
    if (tc === 0) {
      return 0;
    }
    return ((pg - 1) * ps) + 1;
  }

  get endIndex(): number {
    const tc = Number(this.vm.totalCount || 0);
    const ps = Number(this.vm.pageSize || 1);
    const pg = Number(this.vm.page || 1);
    if (tc === 0) {
      return 0;
    }
    const end = pg * ps;
    return end > tc ? tc : end;
  }

  onPageChange(page: number): void {
    if (page && page !== this.vm.page) {
      this.vm.page = page;
      this.load();
    }
  }

  onPageSizeChange(newSize: any): void {
    const size = Number(newSize);
    if (!Number.isFinite(size) || size <= 0) {
      return;
    }
    if (size !== this.vm.pageSize) {
      this.vm.pageSize = size;
      this.vm.page = 1;
      this.load();
    }
  }

  // Format date/time as mm/dd/yyyy HH:mm:ss (local time)
  formatDateTime(value: any): string {
    if (!value) {
      return '';
    }
    const d = (value instanceof Date) ? value : new Date(String(value));
    if (Number.isNaN(d.getTime())) {
      return String(value);
    }
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    const yyyy = d.getFullYear();
    const HH = String(d.getHours()).padStart(2, '0');
    const MM = String(d.getMinutes()).padStart(2, '0');
    const SS = String(d.getSeconds()).padStart(2, '0');
    return `${mm}/${dd}/${yyyy} ${HH}:${MM}:${SS}`;
  }

  // Format decimal numbers with 2 fraction digits and locale grouping
  formatDecimal(value: any): string {
    if (value === null || value === undefined || value === '') {
      return '';
    }
    const n = Number(value);
    if (!Number.isFinite(n)) {
      return String(value);
    }
    try {
      return n.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2});
    } catch {
      return n.toFixed(2);
    }
  }

  // Exact mapping helper: resolve Entity name from AppEntityId using loaded entity list
  getEntityNameById(appEntityId: any): string {
    const id = String(appEntityId ?? '').trim();
    if (!id) {
      return '';
    }
    for (const e of this.vm.entity || []) {
      if (String(e?.id) === id) {
        return String(e?.name ?? '');
      }
    }
    return '';
  }

  // Map Stripe status to Bootstrap contextual badge classes used elsewhere in the app
  getStatusBadge(status: any): string {
    const s = String(status ?? '').toLowerCase();
    if (!s) {
      return 'text-bg-secondary';
    }
    // Common Stripe-like statuses
    if (s.includes('succeed') || s === 'paid' || s === 'completed') {
      return 'text-bg-success';
    }
    if (s.includes('pending') || s.includes('processing') || s.includes('requires')) {
      return 'text-bg-warning';
    }
    if (s.includes('fail') || s.includes('canceled') || s.includes('cancelled') || s.includes('requires_payment_method') || s.includes('requires_action')) {
      return 'text-bg-danger';
    }
    if (s.includes('refunded') || s.includes('dispute') || s.includes('review')) {
      return 'text-bg-info';
    }
    return 'text-bg-secondary';
  }

  private async loadFilterOptions(): Promise<void> {
    // Entities (for typeahead on EntitySelect)
    try {
      const entityResp = await this.tableDataService
        .getData('entity?state=active&pageNumber=0&pageSize=99999')
        .toPromise() as ResponseModel;
      const entities: any[] = (entityResp as any)?.Data || (entityResp as any) || [];
      this.vm.entity = Array.isArray(entities) ? entities : [];
      // Build typeahead source used by EntitySelectComponent
      this.vm.entityName = (this.vm.entity || []).map((e: any) => ({name: e?.name}));
    } catch {
      this.vm.entity = [];
      this.vm.entityName = [];
    }
  }

  // ===== Entity selector handlers (mirroring Payments report) =====
  updateEntity() {
    this.vm.buttonsDisabled = true;
    this.tableDataService
      .getData('entity', '?state=active&pageNumber=0&pageSize=99999')
      .subscribe((result: ResponseModel) => {
        const data: any[] = (result as any)?.Data || (result as any) || [];
        this.vm.entity = Array.isArray(data) ? data : [];
        this.vm.entityName = (this.vm.entity || []).map((e: any) => ({name: e?.name}));
        this.vm.buttonsDisabled = false;
      }, () => {
        this.vm.buttonsDisabled = false;
      });
  }

  selectEntityModal(entityName: string) {
    this.vm.request.entity_id = '';
    for (const e of this.vm.entity || []) {
      if (e?.name === entityName) {
        this.vm.entityInput = entityName;
        this.vm.request.entity_id = e?.id;
        this.vm.report = [];
        break;
      }
    }
  }

  onEntityCleared() {
    this.vm.request.entity_id = '';
    this.vm.entityInput = '';
    this.vm.report = [];
  }

  testEntity() {
    const startId = this.vm.request.entity_id;
    const input = (this.vm.entityInput || '').trim();
    if (!input) {
      this.vm.request.entity_id = '';
    } else {
      const found = (this.vm.entity || []).find((e: any) => e?.name === input);
      this.vm.request.entity_id = found?.id || '';
    }
    if (startId !== this.vm.request.entity_id) {
      this.vm.report = [];
    }
  }

  // Email and print
  setEmailData(): void {
    const fromDate = this.normalizeDate(this.vm.request.fromDate ?? this.vm.fromDateSelect) || '';
    const toDate = this.normalizeDate(this.vm.request.toDate ?? this.vm.toDateSelect) || '';

    const emailDocObj = {} as EmailModel;
    const reportName = 'Stripe Transaction Detail';

    // printing options
    emailDocObj.print_orientation = '1';
    emailDocObj.convert_attachment_to = 'pdf';

    emailDocObj.fromEmail = '';
    emailDocObj.fromName = 'AcctXErp';
    emailDocObj.header_id = '' as any;
    emailDocObj.toEmail = '';
    emailDocObj.body = 'Please see the attached ' + reportName + '.<br><br><br>'
      + 'Best regards,'
      + '<br><br>'
      + 'AcctXErp'
      + '<br><br>';
    emailDocObj.Subject = 'AcctXErp ' + reportName
      + (fromDate || toDate ? ` (${fromDate || '—'} to ${toDate || '—'})` : '');

    try {
      emailDocObj.pdfHtml = this.buildPrintHtml(true);
    } catch {
      emailDocObj.pdfHtml = '';
    }
    emailDocObj.activity_message = reportName;

    // deterministic file name
    const now = new Date();
    const yyyy = String(now.getFullYear());
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const dd = String(now.getDate()).padStart(2, '0');
    emailDocObj.FileName = `Stripe_Transaction_Detail_${yyyy}${mm}${dd}.pdf`;

    this.reportDataToSendInEmail = emailDocObj;

    // Open the shared send-email modal
    const openModal = () => {
      const element = document.getElementById('sendEmailModal') as HTMLElement;
      if (element) {
        const emailModal = new Modal(element);
        emailModal.show();
      }
    };
    setTimeout(openModal, 0);
  }

  printReport(): void {
    const html = this.buildPrintHtml(false);
    this.printHtmlInIframe(html);
  }

  private buildPrintHtml(forEmail: boolean = false): string {
    const rawContent = (this.PrintRoot?.nativeElement?.outerHTML || document.getElementById('excel-export')?.outerHTML || '').toString();
    const content = this.reportPrint.cleanContentHtml(rawContent);
    const from = this.vm?.request?.fromDate || '';
    const to = this.vm?.request?.toDate || '';
    const meta: { label: string; value: string }[] = [];
    if (from || to) {
      meta.push({label: 'Period', value: from && to ? `${from} to ${to}` : (from || to)});
    }
    return this.reportPrint.buildHtml({title: 'Stripe Transaction Detail', contentHtml: content, meta, orientation: 'landscape'});
  }

  private printHtmlInIframe(html: string): void {
    this.reportPrint.printInIframe(html);
  }

  private escapeHtml(text: string): string {
    return (text || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  // Utilities
  toggleRow(row: any): void {
    row.__expanded = !row.__expanded;
  }

  // ------- Drawer helpers & value pickers -------
  openDrawer(row: any, tab: 'summary' | 'amounts' | 'payment' | 'related' | 'json' = 'summary'): void {
    this.selectedRow = row;
    this.drawerTab = tab;
    this.drawerOpen = true;
  }

  closeDrawer(): void {
    this.drawerOpen = false;
    this.drawerTab = 'summary';
  }

  // Template helpers (avoid arrow functions in template expressions)
  displayEntityName(row: any): string {
    const direct = row?.entity?.name;
    if (direct) {
      return direct;
    }
    const id = this.vm?.request?.entity_id ?? '';
    if (!id) {
      return '';
    }
    const found = (this.vm?.entity || []).find((e: any) => e?.id === id);
    return found?.name || '';
  }

  displayBankName(row: any): string {
    const direct = row?.bank?.name;
    if (direct) {
      return direct;
    }
    const id = this.vm?.request?.bank_id ?? '';
    if (!id) {
      return '';
    }
    const found = (this.vm?.bank || []).find((b: any) => b?.id === id);
    return found?.name || '';
  }

  // Print current row: open drawer then trigger print (cannot use setTimeout arrow in template)
  printRow(row: any): void {
    this.openDrawer(row, 'summary');
    setTimeout(() => this.printReport(), 0);
  }

  pick(obj: any, paths: string[]): any {
    for (const p of paths) {
      const val = p.split('.').reduce((acc, key) => (acc ? acc[key] : undefined), obj);
      if (val !== undefined && val !== null) {
        return val;
      }
    }
    return undefined;
  }

  pickNumber(obj: any, paths: string[]): number | null {
    const v = this.pick(obj, paths);
    const n = typeof v === 'string' ? Number(v) : v;
    return Number.isFinite(n) ? Number(n) : null;
  }

  formatAmount(minorUnits: number | null | undefined, currency: string | undefined): string {
    if (!Number.isFinite(minorUnits as any)) {
      return '';
    }
    const cur = (currency || '').toUpperCase();
    // Stripe amounts are minor units; default to 2 decimals for common currencies
    const divisor = 100;
    const major = (minorUnits as number) / divisor;
    try {
      return new Intl.NumberFormat(undefined, {style: 'currency', currency: cur || 'USD'}).format(major);
    } catch {
      return `${major.toFixed(2)} ${cur || ''}`.trim();
    }
  }

  private flatten(obj: any, parentKey = '', res: any = {}): any {
    if (obj === null || obj === undefined) {
      return res;
    }
    if (Array.isArray(obj)) {
      res[parentKey || 'items'] = JSON.stringify(obj);
      return res;
    }
    if (typeof obj !== 'object') {
      res[parentKey] = obj;
      return res;
    }
    Object.keys(obj).forEach((k) => {
      const key = parentKey ? `${parentKey}.${k}` : k;
      const val = obj[k];
      if (val && typeof val === 'object' && !Array.isArray(val)) {
        this.flatten(val, key, res);
      } else if (Array.isArray(val)) {
        res[key] = JSON.stringify(val);
      } else {
        res[key] = val;
      }
    });
    return res;
  }
}
